/**
 * Éditeur visuel du point focal + zoom d'une image — inspiré de Sakinea.
 *
 * - Clique sur l'image pour définir le point focal (croix orange)
 * - Slider zoom (1× à 3×)
 * - 2 previews à droite : aspect 16:9 (hero) + carré 1:1 (card)
 *
 * Émet via onChange({ focal_x, focal_y, zoom }) — pourcentages 0–100 pour
 * les coordonnées, 1.0–3.0 pour le zoom.
 */
import { useRef, useState } from "react";
import { Crosshair, ZoomIn, ZoomOut } from "lucide-react";

export default function FocalPointPicker({
    src,
    focalX = 50,
    focalY = 50,
    zoom = 1,
    onChange,
}) {
    const imgWrapRef = useRef(null);
    const [dragging, setDragging] = useState(false);

    function handlePoint(e) {
        const rect = imgWrapRef.current?.getBoundingClientRect();
        if (!rect) return;
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        onChange?.({
            focal_x: Math.max(0, Math.min(100, x)),
            focal_y: Math.max(0, Math.min(100, y)),
            zoom,
        });
    }

    function setZoom(z) {
        onChange?.({ focal_x: focalX, focal_y: focalY, zoom: z });
    }

    if (!src) {
        return (
            <div className="rounded-lg bg-muted p-6 text-center text-sm text-muted-foreground">
                Téléverse d'abord une image pour activer l'éditeur.
            </div>
        );
    }

    const objectPosition = `${focalX}% ${focalY}%`;
    const transformScale = `scale(${zoom})`;

    return (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start">
            {/* Image cliquable */}
            <div>
                <div
                    ref={imgWrapRef}
                    onMouseDown={(e) => { setDragging(true); handlePoint(e); }}
                    onMouseMove={(e) => dragging && handlePoint(e)}
                    onMouseUp={() => setDragging(false)}
                    onMouseLeave={() => setDragging(false)}
                    onClick={handlePoint}
                    className="relative overflow-hidden rounded-lg border border-border bg-muted cursor-crosshair select-none"
                    style={{ aspectRatio: "16/9" }}
                    title="Clique pour définir le point focal"
                >
                    <img
                        src={src}
                        alt="point focal"
                        className="absolute inset-0 w-full h-full object-cover pointer-events-none transition-transform duration-200"
                        style={{ transform: transformScale, transformOrigin: objectPosition }}
                        draggable={false}
                    />
                    {/* Marker point focal */}
                    <div
                        className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                        style={{ left: `${focalX}%`, top: `${focalY}%` }}
                    >
                        <div className="h-7 w-7 rounded-full border-2 border-white shadow-lg flex items-center justify-center bg-accent">
                            <Crosshair className="h-3.5 w-3.5 text-accent-foreground" />
                        </div>
                    </div>
                </div>
                {/* Slider zoom */}
                <div className="mt-3 flex items-center gap-3 px-1">
                    <ZoomOut className="h-4 w-4 text-muted-foreground shrink-0" />
                    <input
                        type="range"
                        min={1}
                        max={3}
                        step={0.05}
                        value={zoom}
                        onChange={(e) => setZoom(parseFloat(e.target.value))}
                        className="flex-1 accent-primary"
                    />
                    <ZoomIn className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-xs tabular-nums w-12 text-right font-mono text-muted-foreground">
                        {zoom.toFixed(2)}×
                    </span>
                </div>
                <p className="text-xs text-muted-foreground/70 mt-1">
                    Point focal : {Math.round(focalX)}% × {Math.round(focalY)}%
                </p>
            </div>

            {/* Previews */}
            <div className="space-y-3">
                <PreviewBox label="Hero 16:9" src={src} focalX={focalX} focalY={focalY} zoom={zoom} aspect="16/9" />
                <PreviewBox label="Card 1:1" src={src} focalX={focalX} focalY={focalY} zoom={zoom} aspect="1/1" />
            </div>
        </div>
    );
}


function PreviewBox({ label, src, focalX, focalY, zoom, aspect }) {
    return (
        <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground/70 mb-1.5">{label}</p>
            <div
                className="w-40 overflow-hidden rounded-lg border border-border bg-muted"
                style={{ aspectRatio: aspect }}
            >
                <img
                    src={src}
                    alt={label}
                    className="w-full h-full object-cover"
                    style={{
                        objectPosition: `${focalX}% ${focalY}%`,
                        transform: `scale(${zoom})`,
                        transformOrigin: `${focalX}% ${focalY}%`,
                    }}
                />
            </div>
        </div>
    );
}
